import type { Node } from "@xyflow/react";
import {
  buildCanvasTimePages,
  type CanvasTimePage,
} from "@/components/canvas/canvas-time-pages";
import { isWorkspaceFrame } from "@/lib/frame-classification";
import { frameNodeSize } from "@/lib/frame-node-geometry";
import type { FrameData } from "@/lib/frame-data";

import { useCanvasTimePageStore } from "@/stores/canvas-time-page-store";

function visiblePageNodes(nodes: Node<FrameData>[], spaceId?: string | null) {
  return nodes.filter(
    (node) =>
      isWorkspaceFrame(node.data.frameType) &&
      !node.hidden &&
      node.data.metadata?.hidden !== true &&
      (!spaceId || node.data.spaceId === spaceId),
  );
}

function nodeInput(node: Node<FrameData>): string {
  const size = frameNodeSize(node);
  return JSON.stringify([
    node.data.frameType,
    node.data.createdAt,
    node.data.metadata?.pinned === true,
    size.width,
    size.height,
  ]);
}

/** Read established pages, pruning removals without moving surviving members. */
export function getCanvasTimePages(
  nodes: Node<FrameData>[],
  spaceId?: string | null,
  layout = useCanvasTimePageStore.getState().layout,
): CanvasTimePage[] {
  const visible = visiblePageNodes(nodes, spaceId);
  if (
    !layout ||
    layout.spaceId !== (spaceId ?? null) ||
    visible.some((node) => layout.nodeInputs.get(node.id) !== nodeInput(node))
  ) {
    return buildCanvasTimePages(nodes, spaceId);
  }
  const visibleIds = new Set(visible.map((node) => node.id));
  return layout.pages.flatMap((page) => {
    const nodeIds = page.nodeIds.filter((id) => visibleIds.has(id));
    return nodeIds.length > 0
      ? [{ ...page, nodeIds, frameCount: nodeIds.length }]
      : [];
  });
}

/**
 * Capture committed page membership. Omit pages to retain existing membership;
 * pass freshly built pages when explicitly establishing a new layout.
 */
export function rememberCanvasTimePageMembership(
  nodes: Node<FrameData>[],
  spaceId?: string | null,
  pages = getCanvasTimePages(nodes, spaceId),
): void {
  useCanvasTimePageStore.getState().setLayout({
    spaceId: spaceId ?? null,
    pages: pages.map((page) => ({ ...page, nodeIds: [...page.nodeIds] })),
    nodeInputs: new Map(
      visiblePageNodes(nodes, spaceId).map((node) => [
        node.id,
        nodeInput(node),
      ]),
    ),
  });
}
